import type { VideoModel } from './index';
import { sleep } from '../shared/backoff';
import { createScopedLogger } from '../shared/debug-log';
import { nanoid } from 'nanoid';

const CREATE_RETRY_DELAY_MS = 500;
const POLL_RETRY_ATTEMPTS = 3;
const POLL_RETRY_DELAY_MS = 1000;
const EXECUTE_REQUEST_TIMEOUT_MS = 90_000;

function isRetryableNetworkStatus(status: number): boolean {
    return status === 408 || status === 429 || (status >= 500 && status <= 599);
}

function extractErrorDetail(data: any): string {
    if (!data) return '';
    const parts: string[] = [];
    const code = data?.error?.code;
    if (typeof code === 'string' && code) parts.push(code);
    const msg = data?.error?.message || data?.message || data?.msg || data?.detail;
    if (typeof msg === 'string' && msg) parts.push(msg);
    if (!parts.length && typeof data?.error === 'string' && data.error) parts.push(data.error);
    return parts.join(': ');
}

function isAbortError(error: unknown): boolean {
    return (
        error instanceof Error &&
        // fetch abort in Node/DOM usually surfaces as name === 'AbortError'
        (error.name === 'AbortError' || error.message.toLowerCase().includes('abort'))
    );
}

// ==================== 任务创建 ====================
/**
 * 任务创建请求配置
 */
export type CreateTaskConfig = {
    /** 请求 URL */
    url: string;
    /** 请求方法 */
    method: 'GET' | 'POST' | 'PUT';
    /** 请求头 */
    headers: Record<string, string>;
    /** 请求体 */
    body?: Record<string, any> | string;
};

/**
 * 任务创建响应解析结果
 */
export type CreateTaskResult = {
    /** 任务 ID */
    taskId: string;
    /** 原始响应数据 */
    raw?: any;
};

// ==================== 任务轮询 ====================
/**
 * 任务轮询请求配置
 */
export type PollTaskConfig = {
    /** 请求 URL */
    url: string | ((taskId: string) => string);
    /** 请求方法 */
    method: 'GET' | 'POST';
    /** 请求头 */
    headers: Record<string, string>;
    /** 请求体 */
    body?: Record<string, any> | string;
};

/**
 * 任务轮询状态枚举
 */
export enum TaskStatus {
    /** 处理中 */
    PROCESSING = 'processing',
    /** 成功 */
    SUCCESS = 'success',
    /** 失败 */
    FAILED = 'failed',
}

/**
 * 任务轮询响应解析结果
 */
export type PollTaskResult = {
    /** 任务状态 */
    status: TaskStatus;
    /** 视频 URL */
    videoUrl?: string;
    /** 多视频 URL（当 provider 返回数组时） */
    videoUrls?: string[];
    /** 错误信息 */
    error?: string;
    /** 原始响应数据 */
    raw?: any;
};

// ==================== 视频模型生成参数 ====================
/**
 * 视频模型生成参数
 */
export type VideoGenerateParams = {
    prompt: string;
    imagePrompt?: string[];
    videoUri?: string;
    videoUris?: string[];
    audioUri?: string;
    audioUris?: string[];
    duration: number;
    aspectRatio?: string;
    quality?: string;
    modelUi?: Record<string, any>;
    providerOptions?: Record<string, Record<string, any>>;
    userId?: string;
    generationId?: string;
    onTaskCreated?: (taskId: string) => void | Promise<void>;
};

// ==================== 提供商配置 ====================
/**
 * 提供商配置
 */
export type ProviderConfig = {
    /** Base URL 环境变量名 */
    baseUrlEnv: string;
    /** API Key 环境变量名 */
    apiKeyEnv: string;
    /** Debug 环境变量名 */
    debugEnv?: string;
    /** 提供商名称 */
    providerName: string;
};

/**
 * 提供商上下文
 */
export type ProviderContext = {
    baseUrl: string;
    apiKey: string;
};

/**
 * 提供商适配器接口
 */
export interface VideoProviderAdapter {
    /**
     * 任务创建请求配置
     * 支持异步的以便在构建请求前进行预处理
     */
    buildCreateTaskRequest(
        modelId: string,
        params: VideoGenerateParams,
        context: ProviderContext
    ): CreateTaskConfig | Promise<CreateTaskConfig>;

    /**
     * 任务创建响应解析结果, 提取 taskId 并返回
     */
    parseCreateTaskResponse(
        modelId: string,
        response: Response,
        data: any
    ): CreateTaskResult;

    /**
     * 任务轮询请求配置
     */
    buildPollTaskRequest(
        modelId: string,
        taskId: string,
        params: VideoGenerateParams,
        context: ProviderContext
    ): PollTaskConfig;

    /**
     * 任务轮询响应解析结果, 判断状态并提取视频 URL 或错误信息并返回
     */
    parsePollTaskResponse(
        modelId: string,
        response: Response,
        data: any
    ): PollTaskResult;
}

// ==================== 视频模型提供商基类 ====================
/**
 * 视频模型提供商基类
 */
export class BaseVideoProvider {
    protected config: ProviderConfig;
    protected debug: boolean;

    constructor(config: ProviderConfig) {
        this.config = config;
        this.debug = process.env[config.debugEnv || `${config.providerName.toUpperCase()}_DEBUG`] === 'true';
    }

    /**
     * 获取 Base URL
     */
    protected getBaseUrl(): string {
        const base = process.env[this.config.baseUrlEnv];
        if (!base) {
            throw new Error(`Missing ${this.config.baseUrlEnv}`);
        }
        return base.replace(/\/$/, '');
    }

    /**
     * 获取 API Key
     */
    protected getApiKey(): string {
        const key = process.env[this.config.apiKeyEnv];
        if (!key) {
            throw new Error(`Missing ${this.config.apiKeyEnv}`);
        }
        return key;
    }

    /**
     * 创建日志记录器
     */
    protected createLogger(modelId: string, requestId: string, userId?: string) {
        return createScopedLogger({
            tags: [userId, this.config.providerName.toUpperCase(), modelId, requestId],
            debug: this.debug,
        });
    }

    /**
     * 执行 HTTP 请求
     */
    protected async executeRequest(config: CreateTaskConfig | PollTaskConfig): Promise<{ response: Response; data: any }> {
        const url = typeof config.url === 'function' ? config.url('') : config.url;
        const createConfig = config as CreateTaskConfig;
        const method = config.method;
        const headers = config.headers;

        let body: string | undefined;
        if (createConfig.body !== undefined) {
            body = typeof createConfig.body === 'string'
                ? createConfig.body
                : JSON.stringify(createConfig.body);
        }

        const response = await fetch(url, {
            method: method,
            headers: headers,
            body: body,
            signal: AbortSignal.timeout(EXECUTE_REQUEST_TIMEOUT_MS),
        });

        let data: any;
        try {
            const text = await response.text();
            if (text) {
                data = JSON.parse(text);
            }
        } catch {
            data = {};
        }

        return { response: response, data: data };
    }

    /**
     * 创建视频模型实例
     */
    createVideoModel(
        modelId: string,
        adapter: VideoProviderAdapter
    ): VideoModel {
        const createTask = async (params: VideoGenerateParams) => {
            const requestId = params.generationId || nanoid(10);
            const log = this.createLogger(modelId, requestId, params.userId);

            // 0. 准备提供商上下文
            const context: ProviderContext = {
                baseUrl: this.getBaseUrl(),
                apiKey: this.getApiKey(),
            };

            // 1. 创建任务
            log.debug('[create:start]', { params: params });
            const createConfigPromise = adapter.buildCreateTaskRequest(modelId, params, context);
            const createConfig = await Promise.resolve(createConfigPromise);

            log.debug('[create:request]', {
                url: createConfig.url,
                method: createConfig.method,
                body: typeof createConfig.body === 'string'
                    ? createConfig.body
                    : JSON.stringify(createConfig.body),
            });

            let createResp: Response | undefined;
            let createData: any = undefined;
            for (let attempt = 0; attempt < 2; attempt++) {
                try {
                    const result = await this.executeRequest(createConfig);
                    createResp = result.response;
                    createData = result.data;

                    if (!createResp.ok) {
                        log.error('[create:http_error]', {
                            attempt: attempt + 1,
                            status: createResp.status,
                            statusText: createResp.statusText,
                            data: createData,
                        });

                        if (attempt === 0 && isRetryableNetworkStatus(createResp.status)) {
                            log.error('[create:retry_once]', { reason: 'retryable_status', status: createResp.status });
                            await sleep(CREATE_RETRY_DELAY_MS);
                            continue;
                        }

                        const detail = extractErrorDetail(createData);
                        throw new Error(`${modelId} task create failed: ${createResp.status} ${createResp.statusText}${detail ? ` — ${detail}` : ''}`);
                    }

                    break;
                } catch (error) {
                    if (attempt === 0 && !isAbortError(error)) {
                        // fetch 抛错通常表示网络错误/连接失败；这里允许重试一次
                        log.error('[create:request_error]', {
                            attempt: attempt + 1,
                            error: error instanceof Error ? error.message : String(error),
                        });
                        log.error('[create:retry_once]', { reason: 'fetch_error' });
                        await sleep(CREATE_RETRY_DELAY_MS);
                        continue;
                    }
                    throw error;
                }
            }

            if (!createResp) {
                throw new Error(`${modelId} task create failed: missing response`);
            }

            const createResult = adapter.parseCreateTaskResponse(modelId, createResp, createData);
            const taskId = createResult.taskId;

            if (!taskId) {
                log.error('[create:missing_task_id]', { data: createData });
                throw new Error('Failed to get task id');
            }

            log.debug('[create:success]', { taskId: taskId, data: createResult.raw });

            // 触发回调
            if (params.onTaskCreated) {
                await params.onTaskCreated(taskId);
            }

            return { taskId };
        };

        const pollTask = async (taskId: string, params?: VideoGenerateParams) => {
            const safeParams = params || { prompt: '', duration: 0 } as VideoGenerateParams;

            const requestId = safeParams.generationId || taskId || nanoid(10);
            const log = this.createLogger(modelId, requestId, safeParams.userId);

            const context: ProviderContext = {
                baseUrl: this.getBaseUrl(),
                apiKey: this.getApiKey(),
            };

            const pollConfig = adapter.buildPollTaskRequest(modelId, taskId, safeParams, context);
            const pollUrl = typeof pollConfig.url === 'function' ? pollConfig.url(taskId) : pollConfig.url;

            log.debug('[poll:request]', { taskId: taskId, url: pollUrl });

            let pollResp: Response | undefined;
            let pollData: any = undefined;
            for (let attempt = 0; attempt < POLL_RETRY_ATTEMPTS; attempt++) {
                try {
                    const result = await this.executeRequest({
                        ...pollConfig,
                        url: pollUrl,
                    });
                    pollResp = result.response;
                    pollData = result.data;

                    if (!pollResp.ok) {
                        log.error('[poll:http_error]', {
                            attempt: attempt + 1,
                            status: pollResp.status,
                            statusText: pollResp.statusText,
                            data: pollData,
                        });

                        if (attempt < POLL_RETRY_ATTEMPTS - 1 && isRetryableNetworkStatus(pollResp.status)) {
                            log.error('[poll:retry]', { reason: 'retryable_status', status: pollResp.status, attempt: attempt + 1 });
                            await sleep(POLL_RETRY_DELAY_MS);
                            continue;
                        }

                        const detail = extractErrorDetail(pollData);
                        throw new Error(`${modelId} task poll failed: ${pollResp.status} ${pollResp.statusText}${detail ? ` — ${detail}` : ''}`);
                    }

                    break;
                } catch (error) {
                    if (isAbortError(error)) throw error;
                    log.error('[poll:request_error]', {
                        attempt: attempt + 1,
                        error: error instanceof Error ? error.message : String(error),
                    });
                    if (attempt < POLL_RETRY_ATTEMPTS - 1) {
                        log.error('[poll:retry]', { reason: 'fetch_error', attempt: attempt + 1 });
                        await sleep(POLL_RETRY_DELAY_MS);
                        continue;
                    }
                    throw error;
                }
            }

            if (!pollResp) {
                throw new Error(`${modelId} task poll failed: missing response`);
            }

            const pollResult = adapter.parsePollTaskResponse(modelId, pollResp, pollData);
            return pollResult;
        };

        return {
            provider: this.config.providerName,
            modelId: modelId,
            createTask: createTask,
            pollTask: (taskId: string) => pollTask(taskId),
        };
    }
}
